import React, { useEffect, useState } from 'react';
import { SearchIcon, PlusIcon, ChevronLeftIcon, ChevronRightIcon, EyeIcon, EditIcon, Trash2Icon, ImageIcon, InfoIcon, FilterIcon } from 'lucide-react';
import { CollectionRow } from '../../components/admin/collections/CollectionRow';
import { CollectionSkeletonRow } from '../../components/admin/collections/CollectionSkeletonRow';
import { EmptyCollections } from '../../components/admin/collections/EmptyCollections';
import { CreateCollectionModal } from '../../components/admin/collections/CreateCollectionModal';
import { Tooltip } from '../../components/admin/Tooltip';
export const CollectionManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  // Sample collections data - in a real app this would come from an API
  const [collections, setCollections] = useState([{
    id: '1',
    name: 'Student Phones',
    spuCount: 24,
    skuCount: 68,
    individualSkuCount: 5,
    displaySequence: 1,
    isPopular: true,
    type: 'mixed'
  }, {
    id: '2',
    name: 'Business Laptops',
    spuCount: 18,
    skuCount: 42,
    individualSkuCount: 0,
    displaySequence: 2,
    isPopular: true,
    type: 'spu'
  }, {
    id: '3',
    name: 'Gaming Accessories',
    spuCount: 0,
    skuCount: 0,
    individualSkuCount: 42,
    displaySequence: 3,
    isPopular: false,
    type: 'sku'
  }, {
    id: '4',
    name: 'Smart Home Devices',
    spuCount: 15,
    skuCount: 32,
    individualSkuCount: 0,
    displaySequence: 4,
    isPopular: false,
    type: 'spu'
  }, {
    id: '5',
    name: 'Refurbished iPhones',
    spuCount: 8,
    skuCount: 24,
    individualSkuCount: 7,
    displaySequence: 5,
    isPopular: true,
    type: 'mixed'
  }]);
  // Filter collections based on search query and active filter
  const filteredCollections = collections.filter(collection => {
    const matchesSearch = collection.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (activeFilter === 'all') return matchesSearch;
    if (activeFilter === 'spu-heavy') return matchesSearch && collection.spuCount > collection.individualSkuCount;
    if (activeFilter === 'sku-specific') return matchesSearch && collection.individualSkuCount > 0 && collection.spuCount === 0;
    if (activeFilter === 'mixed') return matchesSearch && collection.spuCount > 0 && collection.individualSkuCount > 0;
    return matchesSearch;
  });
  // Pagination logic
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredCollections.length / itemsPerPage);
  const paginatedCollections = filteredCollections.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const handlePageChange = page => {
    setCurrentPage(page);
  };
  const handleSearch = e => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };
  const handleTogglePopular = (id, newStatus) => {
    setCollections(collections.map(collection => collection.id === id ? {
      ...collection,
      isPopular: newStatus
    } : collection));
  };
  const handleSequenceChange = (id, newSequence) => {
    setCollections(collections.map(collection => collection.id === id ? {
      ...collection,
      displaySequence: parseInt(newSequence)
    } : collection));
  };
  const handleDeleteCollection = id => {
    if (window.confirm('Are you sure you want to delete this collection?')) {
      setCollections(collections.filter(collection => collection.id !== id));
    }
  };
  const handleCreateCollection = newCollection => {
    setCollections([...collections, {
      id: (collections.length + 1).toString(),
      ...newCollection,
      spuCount: 0,
      skuCount: 0,
      individualSkuCount: 0
    }]);
    setShowCreateModal(false);
  };
  const handleFilterChange = filter => {
    setActiveFilter(filter);
    setCurrentPage(1); // Reset to first page when changing filters
  };
  // Simulate loading state
  const loadData = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };
  // Uncomment to test loading state
  // useEffect(() => {
  //   loadData()
  // }, [])
  return <div className="space-y-6" data-id="element-1474">
      {/* Header Section */}
      <div className="flex justify-between items-center" data-id="element-1475">
        <div data-id="element-1476">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1477">
            Collection Management
          </h1>
          <p className="text-sm text-gray-500" data-id="element-1478">
            Create and manage product collections for your store
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center" data-id="element-1479">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-1480" />
          Create New Collection
        </button>
      </div>
      {/* Search and Filter Section */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200" data-id="element-1481">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1482">
          <div className="relative flex-grow" data-id="element-1483">
            <input type="text" placeholder="Search collections..." value={searchQuery} onChange={handleSearch} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1484" />
            <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-1485" />
          </div>
          <div className="flex items-center space-x-2" data-id="element-1486">
            <button onClick={() => handleFilterChange('all')} className={`px-3 py-1 text-sm rounded-md ${activeFilter === 'all' ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1487">
              All Collections
            </button>
            <button onClick={() => handleFilterChange('spu-heavy')} className={`px-3 py-1 text-sm rounded-md ${activeFilter === 'spu-heavy' ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1488">
              SPU Heavy
            </button>
            <button onClick={() => handleFilterChange('sku-specific')} className={`px-3 py-1 text-sm rounded-md ${activeFilter === 'sku-specific' ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1489">
              SKU Specific
            </button>
            <button onClick={() => handleFilterChange('mixed')} className={`px-3 py-1 text-sm rounded-md ${activeFilter === 'mixed' ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1490">
              Mixed
            </button>
          </div>
        </div>
      </div>
      {/* Collections Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-1491">
        {filteredCollections.length === 0 && !isLoading ? <EmptyCollections onCreateNew={() => setShowCreateModal(true)} data-id="element-1492" /> : <div className="overflow-x-auto" data-id="element-1493">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1494">
              <thead className="bg-gray-50" data-id="element-1495">
                <tr data-id="element-1496">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1497">
                    Collection Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1498">
                    <div className="flex items-center" data-id="element-1499">
                      Items Count
                      <Tooltip content="Shows the breakdown of SPUs (Standard Product Units) and SKUs (Stock Keeping Units) in this collection" data-id="element-1500">
                        <InfoIcon className="h-3.5 w-3.5 ml-1 text-gray-400" data-id="element-1501" />
                      </Tooltip>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1502">
                    Display Sequence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1503">
                    Popular
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1504">
                    Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1505">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1506">
                {isLoading ? Array.from({
              length: 5
            }).map((_, index) => <CollectionSkeletonRow key={index} data-id="element-1507" />) : paginatedCollections.map(collection => <CollectionRow key={collection.id} collection={collection} onTogglePopular={handleTogglePopular} onSequenceChange={handleSequenceChange} onDelete={handleDeleteCollection} data-id="element-1508" />)}
              </tbody>
            </table>
          </div>}
        {/* Pagination */}
        {filteredCollections.length > 0 && !isLoading && <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-1509">
            <div className="text-sm text-gray-500" data-id="element-1510">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredCollections.length)}{' '}
              of {filteredCollections.length} collections
            </div>
            <div className="flex items-center space-x-2" data-id="element-1511">
              <button onClick={() => handlePageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1512">
                <ChevronLeftIcon className="h-5 w-5" data-id="element-1513" />
              </button>
              {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => handlePageChange(page)} className={`px-3 py-1 rounded-md ${currentPage === page ? 'bg-blue-100 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1514">
                  {page}
                </button>)}
              <button onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1515">
                <ChevronRightIcon className="h-5 w-5" data-id="element-1516" />
              </button>
            </div>
          </div>}
      </div>
      {/* Create Collection Modal */}
      {showCreateModal && <CreateCollectionModal onClose={() => setShowCreateModal(false)} onCreate={handleCreateCollection} data-id="element-1517" />}
    </div>;
};